import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { breadcrumb } from '../../admin/layout/header/header.component';
import { DataService } from '../../services/data.service';
import { MessageService } from 'primeng/api';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GlobalConstants, ImageTypes } from '../../services/global-constants';
import { PermissionHandlerService } from 'src/app/services/permission-handler.service';
import { TranslatorPipe } from '../../services/translator.pipe';

// Order interfaces
interface OrderCustomer {
  name: string;
  email: string;
  phone: string;
  organization: string;
  userId: string;
}

interface OrderProductService {
  productId: string;
  productName: string;
  productType: string;
  unitPrice: number | string; // Allow both number and string (e.g., "not decided yet")
  subtotal: number | string;
  vatAmount: number | string;
  totalAmount: number | string;
  currency: string;
}

interface OrderHistoryItem {
  type: 'COMMENT' | 'ATTACHMENT' | 'STATUS_CHANGE';
  createdAt: string;
  createdBy: string | { $oid: string }; // Can be string or MongoDB ObjectId
  comments?: string;
  attachment?: string;
  status?: string; // For status change items
  _id: string;
}

interface User {
  _id: string;
  name: {
    first: string;
    middle?: string;
    last: string;
  } | string;
}

interface OrderDetails {
  _id: string;
  orderId: string;
  customer: OrderCustomer;
  productService: OrderProductService;
  orderStatus: string;
  orderNotes: string;
  orderHistory: OrderHistoryItem[];
  isActive: boolean;
  createdBy: string;
  attachments: any[];
  survey?: any; // Survey data
  createdAt: string;
  updatedAt: string;
  __v: number;
}

@Component({
  selector: 'app-order-details',
  templateUrl: './order-details.component.html',
  styleUrl: './order-details.component.scss'
})
export class OrderDetailsComponent implements OnInit {
  orderDetails: OrderDetails | null = null;
  loading = true;
  error = '';
  
  // Route parameters
  uid: string = '';
  email: string = '';

  // Breadcrumbs for header
  breadcrumbs: breadcrumb[] = [
    {
      name: this.translate('Services'),
      link: '/products-view',
      visible: true
    },
    {
      name: this.translate('Order Details'),
      link: '',
      visible: true
    }
  ];

  // New properties for order history input
  historyForm: FormGroup;
  selectedHistoryType: 'COMMENT' | 'ATTACHMENT' = 'COMMENT';
  uploadedFileUrl: string = '';
  uploadedFileName: string = '';
  uploadedFileType: string = '';
  isSubmittingHistory: boolean = false;
  isUpdatingStatus: boolean = false;
  
  statusMenuItems: any[] = [];
  
  userCache: Map<string, User> = new Map();
  
  // Image types for file upload
  imageTypes = ImageTypes;
  
  // Static files URL
  staticFilesURL = GlobalConstants.staticFilesURL; // Update this based on your environment

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataService: DataService,
    private messageService: MessageService,
    private fb: FormBuilder,
    public permissionService: PermissionHandlerService
  ) {
    // Initialize the history form
    this.historyForm = this.fb.group({
      comment: ['', Validators.required]
    });
  }

  ngOnInit() {
    // Get parameters from route
    this.route.params.subscribe(params => {
      this.uid = params['uid'] || '';
      // this.email = params['email'] || '';
      
      if (this.uid) {
        this.loadOrderDetails();
      } else {
        this.error = this.translate('Missing required parameters (id)');
        this.loading = false;
      }
    });
  }

  async loadOrderDetails() {
    try {
      this.loading = true;
      this.error = '';
      
      let response;
      
      // Try to get orders by customer user ID first
      if (this.uid) {
        response = await this.dataService.getData(`orders/customer-user/${this.uid}/1/10`);
        
        if (!response.error && response.data) {
          const orders = response.data?.orders || response.data || [];
          const matchingOrder = orders.length > 0 ? orders[0] : null;
          // ord
          
          if (matchingOrder) {
            this.orderDetails = matchingOrder;
            console.log('Order details loaded by user ID:', this.orderDetails);
            this.buildStatusMenuItems();
            await this.loadUserNamesForHistory();
            return;
          }
        }
      }
      
      // Fallback: Try to get orders by customer email
      // if (this.email) {
      //   response = await this.dataService.getData(`orders/customer/${this.email}/1/10`);
        
      //   if (!response.error && response.data) {
      //     const orders = response.data?.orders || response.data || [];
      //     // Get the most recent order for this email
      //     const latestOrder = orders.length > 0 ? orders[0] : null;
          
      //     if (latestOrder) {
      //       this.orderDetails = latestOrder;
      //       console.log('Order details loaded by email:', this.orderDetails);
      //       // Load user names for order history
      //       await this.loadUserNamesForHistory();
      //       return;
      //     }
      //   }
      // }
      
      // If no order found through either method
      this.error = this.translate('No order found for the specified id');
      this.messageService.add({
        severity: 'warn',
        summary: this.translate('No Order Found'),
        detail: this.error
      });
      
    } catch (error) {
      console.error('Error loading order details:', error);
      this.error = this.translate('An error occurred while loading order details');
      this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.error
        });
    } finally {
      this.loading = false;
    }
  }

  async loadUserNamesForHistory() {
    if (!this.orderDetails?.orderHistory) return;

    // Extract user IDs from order history, handling both string and ObjectId formats
    const userIds: string[] = [...new Set(this.orderDetails.orderHistory
      .map(item => {
        const createdBy = item.createdBy;
        // Handle MongoDB ObjectId format: {"$oid": "actual_id"}
        if (createdBy && typeof createdBy === 'object' && createdBy.$oid) {
          return createdBy.$oid;
        }
        // Handle string format
        if (typeof createdBy === 'string') {
          return createdBy;
        }
        return null;
      })
      .filter((userId): userId is string => userId !== null && userId !== 'undefined' && userId !== 'null' && userId.trim() !== '')
    )];
    
    console.log('Extracted User IDs to fetch:', userIds); // Debug log
    
    
    // Fetch user information for each unique user ID
    for (const userId of userIds) {
      if (!this.userCache.has(userId)) {
        try {
          console.log(`Fetching user data for ID: ${userId}`); // Debug log
          const userResponse = await this.dataService.getData(`user/${userId}`);
          if (!userResponse.error && userResponse.data) {
            this.userCache.set(userId, userResponse.data);
            console.log(`Successfully cached user: ${userId}`, userResponse.data); // Debug log
          }
        } catch (error) {
          console.error(`Error fetching user ${userId}:`, error);
          // Set a fallback user object
          this.userCache.set(userId, { _id: userId, name: 'Unknown User' });
        }
      }
    }
  }

  getUserName(userId: string | { $oid: string }): string {
    // Handle MongoDB ObjectId format: {"$oid": "actual_id"}
    let actualUserId: string;
    if (userId && typeof userId === 'object' && userId.$oid) {
      actualUserId = userId.$oid;
    } else if (typeof userId === 'string') {
      actualUserId = userId;
    } else {
      return 'Unknown User';
    }
    
    // Handle invalid user IDs
    if (!actualUserId || actualUserId === 'undefined' || actualUserId === 'null' || actualUserId.trim() === '') {
      return 'Unknown User';
    }
    
    const user = this.userCache.get(actualUserId);
    if (!user) return 'Unknown User';
    
    // Handle nested name structure from API
    if (user.name && typeof user.name === 'object') {
      const { first, middle, last } = user.name;
      const nameParts = [first, middle, last].filter(part => part && part.trim());
      return nameParts.join(' ') || 'Unknown User';
    }
    
    // Handle simple name string
    return user.name || 'Unknown User';
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatCurrency(amount: number | string, currency: string = 'SAR'): string {
    // If amount is not numeric, return it as-is (text)
    if (typeof amount === 'string' || (typeof amount === 'number' && isNaN(amount))) {
      const strAmount = String(amount);
      // Check if it's a valid numeric string
      const numValue = parseFloat(strAmount);
      if (isNaN(numValue) || !isFinite(numValue) || strAmount.trim() === '') {
        return strAmount; // Return text as-is
      }
      amount = numValue; // Convert to number
    }
    
    // Format numeric amount
    const numAmount = typeof amount === 'number' ? amount : parseFloat(String(amount));
    
    const currencyMap: { [key: string]: string } = {
      'ر.س': 'SAR',
      'ر.س.': 'SAR',
      'SAR': 'SAR'
    };
    
    const isoCurrency = currencyMap[currency] || currency;
    
    if (isoCurrency.length === 3 && /^[A-Z]{3}$/.test(isoCurrency)) {
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: isoCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numAmount);
      
      if (isoCurrency === 'SAR') {
        return formatted.replace(/SAR|ر\.س\.?|\$|€|£|ر\.س/g, '').trim();
      }
      return formatted;
    } else {
      const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numAmount);
      return `${formatted} ${currency}`;
    }
  }

  isValidNumber(value: number | string | null | undefined): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    
    // If it's already a number, check if it's valid
    if (typeof value === 'number') {
      return !isNaN(value) && isFinite(value);
    }
    
    // If it's a string, try to parse it
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') {
        return false;
      }
      const numValue = parseFloat(trimmed);
      return !isNaN(numValue) && isFinite(numValue);
    }
    
    return false;
  }

  getHistoryTypeClass(type: string): string {
    switch (type) {
      case 'COMMENT':
        return 'history-type-comment';
      case 'ATTACHMENT':
        return 'history-type-attachment';
      case 'STATUS_CHANGE':
        return 'history-type-status';
      default:
        return 'history-type-default';
    }
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'active':
        return 'status-active';
      case 'pending':
        return 'status-pending';
      case 'completed':
        return 'status-completed';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  }

  refreshOrder() {
    this.loadOrderDetails();
  }

  // New methods for order history management
  onHistoryTypeChange(type: 'COMMENT' | 'ATTACHMENT') {
    this.selectedHistoryType = type;
    if (type === 'ATTACHMENT') {
      this.historyForm.get('comment')?.clearValidators();
    } else {
      this.historyForm.get('comment')?.setValidators([Validators.required]);
    }
    this.historyForm.get('comment')?.updateValueAndValidity();
  }

  onFileUploaded(response: any) {
    if (response.error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Upload Failed',
        detail: response.message
      });
    } else {
      this.uploadedFileUrl = response;
      // Extract filename from URL or use a default name
      this.uploadedFileName = this.extractFileNameFromUrl(response) || 'Uploaded Document';
      this.uploadedFileType = this.getFileTypeFromUrl(response);
      
      this.messageService.add({
        severity: 'success',
        summary: this.translate('File Uploaded'),
        detail: this.translate('File uploaded successfully')
      });
    }
  }

  extractFileNameFromUrl(url: string): string {
    if (!url) return '';
    const urlParts = url.split('/');
    const fileName = urlParts[urlParts.length - 1];
    return fileName || 'Document';
  }

  getFileTypeFromUrl(url: string): string {
    if (!url) return '';
    const extension = url.split('.').pop()?.toLowerCase() || '';
    return extension;
  }

  getFileIcon(fileType: string): string {
    switch (fileType) {
      case 'pdf':
        return 'pi-file-pdf';
      case 'doc':
      case 'docx':
        return 'pi-file-word';
      case 'xls':
      case 'xlsx':
        return 'pi-file-excel';
      case 'ppt':
      case 'pptx':
        return 'pi-file-powerpoint';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'pi-image';
      case 'txt':
        return 'pi-file';
      default:
        return 'pi-file';
    }
  }

  openDocument(url: string) {
    url = this.staticFilesURL + url;
    if (url) {
      window.open(url, '_blank');
    }
  }

  downloadDocument(url: string, fileName: string) {
    url = this.staticFilesURL + url;
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'document';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  async submitHistoryItem() {
    if (this.isSubmittingHistory) return;

    if (this.selectedHistoryType === 'COMMENT') {
      if (this.historyForm.invalid) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate('Validation Error'),
          detail: this.translate('Please enter a comment')
        });
        return;
      }
    } else if (this.selectedHistoryType === 'ATTACHMENT') {
      if (!this.uploadedFileUrl) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate('Validation Error'),
          detail: this.translate('Please upload a file')
        });
        return;
      }
    }

    this.isSubmittingHistory = true;

    try {
      let result;
      
      if (this.selectedHistoryType === 'COMMENT') {
        // Use the comment endpoint
        const commentData = {
          comments: this.historyForm.value.comment
        };

        result = await this.dataService.postData(`orders/comment/${this.orderDetails?._id}`, commentData, 'POST');
      } else if (this.selectedHistoryType === 'ATTACHMENT') {
        // Use the history-attachment endpoint
        const attachmentData = {
          attachment: this.uploadedFileUrl
        };
        result = await this.dataService.postData(`orders/history-attachment/${this.orderDetails?._id}`, attachmentData, 'POST');
      }
      
      if (result.error) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: result.msg || this.translate('Failed to add history item')
        });
      } else {
        this.messageService.add({
          severity: 'success',
          summary: this.translate('Success'),
          detail: this.translate('History item added successfully')
        });
        
        // Reset form and reload order details
        this.resetHistoryForm();
        this.loadOrderDetails();
      }
    } catch (error) {
      console.error('Error adding history item:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('An error occurred while adding history item')
      });
    } finally {
      this.isSubmittingHistory = false;
    }
  }

  async updateOrderStatus(status: 'ACCEPTED' | 'UPDATE' | 'UPDATE_SURVEY' | 'REJECTED' | 'PENDING' | 'CANCELED') {
    if (this.isUpdatingStatus) return;

    this.isUpdatingStatus = true;

    try {
      // First update the order status
      const statusData = {
        orderStatus: status
      };

      const statusResult = await this.dataService.postData(`orders/status/${this.orderDetails?._id}`, statusData, 'PUT');
      
      if (statusResult.error) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: statusResult.msg || this.translate('Failed to update order status')
        });
        return;
      }

      // Then add comment or attachment if there's any data in the form
      let historyResult = null;
      
      if (this.selectedHistoryType === 'COMMENT' && this.historyForm.get('comment')?.value?.trim()) {
        // Add comment
        const commentData = {
          comments: this.historyForm.get('comment')?.value
        };
        historyResult = await this.dataService.postData(`orders/comment/${this.orderDetails?._id}`, commentData, 'POST');
      } else if (this.selectedHistoryType === 'ATTACHMENT' && this.uploadedFileUrl) {
        // Add attachment
        const attachmentData = {
          attachment: this.uploadedFileUrl
        };
        historyResult = await this.dataService.postData(`orders/history-attachment/${this.orderDetails?._id}`, attachmentData, 'POST');
      }

      // Check if history item was added successfully
      if (historyResult && historyResult.error) {
        this.messageService.add({
          severity: 'warning',
          summary: this.translate('Status Updated'),
          detail: this.translate(`Order ${status.toLowerCase()} successfully, but failed to add history item`)
        });
      } else if (historyResult) {
        this.messageService.add({
          severity: 'success',
          summary: this.translate('Success'),
          detail: this.translate(`Order ${status.toLowerCase()} and history item added successfully`)
        });
        // Reset form after successful submission
        this.resetHistoryForm();
      } else {
        this.messageService.add({
          severity: 'success',
          summary: this.translate('Success'),
          detail: this.translate(`Order ${status.toLowerCase()} successfully`)
        });
      }
      
      if (status === 'REJECTED' || status === 'ACCEPTED' || status === 'CANCELED') {
        setTimeout(() => {
          this.router.navigate(['/orders']);
        }, 1000);
        return;
      }
      
      this.loadOrderDetails();
    } catch (error) {
      console.error('Error updating order status:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('An error occurred while updating order status')
      });
    } finally {
      this.isUpdatingStatus = false;
    }
  }

  resetHistoryForm() {
    this.historyForm.reset();
    this.selectedHistoryType = 'COMMENT';
    this.uploadedFileUrl = '';
    this.uploadedFileName = '';
    this.uploadedFileType = '';
    this.historyForm.get('comment')?.setValidators([Validators.required]);
    this.historyForm.get('comment')?.updateValueAndValidity();
  }

  // Image modal properties
  showImageModal: boolean = false;
  selectedImageUrl: string = '';
  selectedImageName: string = '';

  // Survey modal properties
  showSurveyModal: boolean = false;
  surveyData: any = null;
  isSavingSurvey: boolean = false;

  // Status severity mapping
  getStatusSeverity(status: string): string {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'ACCEPTED': return 'success';
      case 'UPDATE': return 'warning';
      case 'UPDATE_SURVEY': return 'warning';
      case 'REJECTED': return 'danger';
      case 'CANCELED': return 'secondary';
      default: return 'info';
    }
  }

  // Get history type label
  getHistoryTypeLabel(type: string): string {
    switch (type) {
      case 'COMMENT': return this.translate('Comment');
      case 'ATTACHMENT': return this.translate('Attachment');
      case 'STATUS_CHANGE': return this.translate('Status Change');
      default: return type;
    }
  }

  // Check if file is an image
  isImageFile(url: string): boolean {
    if (!url) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const lowerUrl = url.toLowerCase();
    return imageExtensions.some(ext => lowerUrl.includes(ext));
  }

  // Get full image URL
  getFullImageUrl(url: string): string {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${this.staticFilesURL}${url}`;
  }

  // Open image modal
  openImageModal(imageUrl: string): void {
    this.selectedImageUrl = imageUrl;
    this.selectedImageName = this.extractFileNameFromUrl(imageUrl);
    this.showImageModal = true;
  }

  // Close image modal
  closeImageModal(): void {
    this.showImageModal = false;
    this.selectedImageUrl = '';
    this.selectedImageName = '';
  }

  // Open survey modal
  openSurveyModal(): void {
    if (this.orderDetails && this.orderDetails.survey) {
      this.surveyData = this.orderDetails.survey;
      this.showSurveyModal = true;
    }
  }

  // Close survey modal
  closeSurveyModal(): void {
    this.showSurveyModal = false;
    this.surveyData = null;
  }

  // Check if survey should be readonly
  isSurveyReadonly(): boolean {
    // Admin can never edit survey
    if (this.permissionService.hasPermission('ORDER', 'u')) {
      return true;
    }
    // User can only edit when status is UPDATE_SURVEY
    return this.orderDetails?.orderStatus !== 'UPDATE_SURVEY';
  }

  // Check if user can edit survey (for save button visibility)
  canEditSurvey(): boolean {
    // Admin can never edit
    if (this.permissionService.hasPermission('ORDER', 'u')) {
      return false;
    }
    // User can edit only when status is UPDATE_SURVEY
    return this.orderDetails?.orderStatus === 'UPDATE_SURVEY';
  }

  // Save survey and update order status
  async saveSurveyAndUpdateOrder(): Promise<void> {
    if (this.isSavingSurvey || this.isUpdatingStatus || !this.surveyData || !this.orderDetails) {
      return;
    }

    this.isSavingSurvey = true;

    try {
      // Update the order with modified survey questions and status (survey is part of order table)
      const orderUpdateData = {
        survey: this.surveyData,  // Include the entire survey object with updated questions
        orderStatus: 'PENDING',
        _id: this.orderDetails._id

      };

      // Update the order with survey questions and status using PUT /orders
      const orderResult = await this.dataService.postData(
        `orders/`,
        orderUpdateData,
        'PUT'
      );

      if (orderResult.error) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: orderResult.msg || this.translate('Failed to update order')
        });
        this.isSavingSurvey = false;
        return;
      }

      // Show success message
      this.messageService.add({
        severity: 'success',
        summary: this.translate('Success'),
        detail: this.translate('Survey updated and order status changed to pending')
      });

      // Close the modal and reload order details
      this.isSavingSurvey = false;
      this.closeSurveyModal();
      await this.loadOrderDetails();

    } catch (error) {
      console.error('Error saving survey and updating order:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('An error occurred while saving survey')
      });
      this.isSavingSurvey = false;
    }
  }

  // Go back to orders
  goBack(): void {
    this.router.navigate(['/orders']);
  
  }

  translate(key: string): string {
    let translator: TranslatorPipe = new TranslatorPipe();
    return translator.transform(key);
  }


  showAddInformationCard(): boolean {
    // Don't show if order is accepted, permanently rejected, or canceled
    if(this.orderDetails?.orderStatus === 'ACCEPTED' || this.orderDetails?.orderStatus === 'REJECTED' || this.orderDetails?.orderStatus === 'CANCELED'){
      return false;
    }

    // Show for PENDING if admin has permission
    if(this.orderDetails?.orderStatus === 'PENDING' && !!this.orderDetails){
      if(this.permissionService.hasPermission('ORDER', 'u')){
        return true;
      }
    }

    // Show for UPDATE status - user can update form/survey and attach documents
    if(this.orderDetails?.orderStatus === 'UPDATE' && !this.permissionService.hasPermission('ORDER', 'u')){
      return true;
    }

    // Show for other statuses if user doesn't have update permission
    if(this.orderDetails?.orderStatus !== 'PENDING' && this.orderDetails?.orderStatus !== 'UPDATE_SURVEY' && this.orderDetails?.orderStatus !== 'UPDATE' && !this.permissionService.hasPermission('ORDER', 'u')){
      return true;
    }

    return false;
  }

  isAcceptedOrRejected(): boolean {
    if (!this.orderDetails) return false;
    const status = this.orderDetails.orderStatus?.toUpperCase().trim();
    return status === 'ACCEPTED' || status === 'REJECTED';
  }

  buildStatusMenuItems(): void {
    this.statusMenuItems = [];
    
    if (!this.orderDetails) return;
    
    if (this.isAcceptedOrRejected() && this.permissionService.hasPermission('ORDER', 'u')) {
      this.statusMenuItems.push({
        label: this.translate('Request Update'),
        icon: 'pi pi-refresh',
        command: () => {
          this.updateOrderStatus('PENDING');
        },
        disabled: this.isUpdatingStatus
      });
      
      this.statusMenuItems.push({
        label: this.translate('Cancel Order'),
        icon: 'pi pi-ban',
        command: () => {
          this.updateOrderStatus('CANCELED');
        },
        disabled: this.isUpdatingStatus
      });
    }
  }

  async copyEmailToClipboard(email: string) {
    if (!email) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate('Warning'),
        detail: this.translate('No email address to copy')
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(email);
      this.messageService.add({
        severity: 'success',
        summary: this.translate('Success'),
        detail: this.translate('Email copied to clipboard')
      });
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = email;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          this.messageService.add({
            severity: 'success',
            summary: this.translate('Success'),
            detail: this.translate('Email copied to clipboard')
          });
        } else {
          throw new Error('Copy command failed');
        }
      } catch (fallbackErr) {
        document.body.removeChild(textArea);
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('Failed to copy email to clipboard')
        });
      }
    }
  }

  truncateEmail(email: string, maxLength: number = 20): string {
    if (!email) return '';
    if (email.length <= maxLength) return email;
    return email.substring(0, maxLength) + '...';
  }

}